/**
 * Out-of-home placements from an upload (pure; runs in the browser and in tests).
 *
 * A placement is a point and a geofence radius. The radius comes from the row's
 * own radius column when it has one, else from the media type by the iOOH venue
 * standard: the `iooh` profile of mfour-agents `workers/ooh/location_radius_rules.py`
 * (Confluence "iOOH Venue Radius Information", OP/509968920). Substring match on
 * the type, case-insensitive, first rule wins, 100 m when nothing matches.
 *
 * These are geofence sizes, the same ones fielding uses to trigger the exposed
 * survey, not funnel rates, so they live here and not on the rate card.
 */
import Papa from 'papaparse';

import { MAX_OOH_PLACEMENTS, type OohExposureCriterion } from '../engine/request';

export type Placement = OohExposureCriterion['placements'][number];

export const DEFAULT_RADIUS_METERS = 100;

/** mfour-agents `DEFAULT_RADIUS_RULES` (profile `iooh`), in order. */
export const IOOH_RADIUS_RULES: readonly (readonly [string, number])[] = [
    ['bus shelter', 30],
    ['bus stop', 30],
    ['news stand', 30],
    ['phone kiosk', 30],
    ['links kiosk', 30],
    ['kiosk', 30],
    ['shelter', 30],
    ['faregate', 30],
    ['subway', 20],
    ['taxi', 30],
    ['transit', 30],
    ['train', 30],
    ['wallscape', 50],
    ['wall', 50],
    ['street sheet', 50],
    ['eye level', 50],
    ['urban panel', 50],
    ['digital spectacular', 100],
    ['spectacular', 100],
    ['bulletin', 100],
    ['billboard', 100],
    ['poster', 100],
    ['liveboard', 100],
    ['grocery', 50],
    ['gym', 50],
    ['gas', 50],
    ['casual dining', 50],
    ['dining', 50],
    ['restaurant', 50],
    ['qsr', 50],
    ['bar', 50],
    ['retail', 50],
    ['salon', 50],
    ['convenience', 50],
    ['mall', 50],
    ['office', 50],
    ['stadium', 100],
    ['arena', 100],
    ['airport', 100],
];

export function radiusForType(type: string | undefined): number {
    const text = (type ?? '').trim().toLowerCase();
    for (const [needle, meters] of IOOH_RADIUS_RULES) if (text.includes(needle)) return meters;
    return DEFAULT_RADIUS_METERS;
}

const LAT = ['lat', 'latitude', 'y'];
const LNG = ['lng', 'lon', 'long', 'longitude', 'x'];
export const TYPE_HEADERS = [
    'type',
    'media type',
    'media',
    'format',
    'venue',
    'venue type',
    'location',
    'unit type',
];
export const RADIUS_HEADERS = [
    'radius',
    'radius m',
    'radius meters',
    'radius (m)',
    'radius_m',
    'radius_meters',
    'geofence',
];

/**
 * A station list's name columns (GSTV, 2026-10-14): the Bid Desk drops a control
 * station whose store name, and a test station whose fuel brand, is not known.
 */
const STORE_NAME = ['store name', 'store', 'station name'];
const BRAND_FUEL = ['brand fuel', 'fuel brand', 'brand'];
/** A name that says nobody knows it. */
export const UNKNOWN_NAME = /^(undetermined|unknown|n\/?a|none|null|-+|\?+)?$/i;

const norm = (h: string) => h.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

export function columnIndex(headers: string[], names: string[]): number {
    const wanted = names.map(norm);
    return headers.findIndex((h) => wanted.includes(norm(h)));
}

/** A column that says which group a row is in (a GSTV list marks each station Test or Control). */
const GROUP = [
    'list',
    'list name',
    'group',
    'cell',
    'test/control',
    'test control',
    'test or control',
    'panel',
    'segment',
    'condition',
    'exposure',
    'station type',
];

export type ListRole = 'test' | 'control';

/** Test or control from a word: a sheet name, a file name, or a group cell. Null when it says neither. */
export function roleOf(text: string): ListRole | null {
    const t = text.toLowerCase();
    if (/\bcontrol\b|\bctrl\b|unexposed|non[- ]?exposed/.test(t)) return 'control';
    if (/\btest\b|\bexposed\b|\bexposure\b/.test(t)) return 'test';
    return null;
}

/**
 * A list's media type when the file has no type column: GSTV's lists are gas
 * stations, so the radius is the iOOH standard's for gas (50 m), not the 100 m
 * default.
 */
export function fallbackTypeOf(name: string): string | undefined {
    return /gstv|gas/i.test(name) ? 'gas station' : undefined;
}

export interface ParsedPlacements {
    placements: Placement[];
    /** Row-level problems, by spreadsheet row number (header is row 1). */
    errors: string[];
    /** How many radii came from the media type rather than a radius column. */
    radiusFromType: number;
    /** Repeated rows of the same place left out, when the list was over the cap only by repeats. */
    duplicatesDropped?: number;
    /** Each placement's group, when the file has a test / control column (same order as placements). */
    roles?: (ListRole | null)[];
    /**
     * Whether each placement's store name and fuel brand are unknown (same
     * order as placements), when the file has either column; null where it has
     * no such column. Which one drops a station depends on its group.
     */
    unknownNames?: { store: boolean | null; brand: boolean | null }[];
}

const LAT_WORDS = new Set(['lat', 'latitude']);
const LNG_WORDS = new Set(['lng', 'lon', 'long', 'longitude']);

/**
 * The latitude and longitude columns of a header row (UAT UI-040, a GSTV
 * station list read as text): an exact name first, then a header holding the
 * word ("Station Latitude", "LAT_DD"), then, failing both, the two columns whose
 * values below look like US coordinates (latitude 17 to 72, longitude -180 to
 * -60, with decimals). -1 each when none is found.
 */
export function coordinateColumns(rows: string[][], header: number): { lat: number; lng: number } {
    const headers = (rows[header] ?? []).map((c) => String(c ?? ''));
    const byWord = (words: Set<string>) =>
        headers.findIndex((h) =>
            h
                .toLowerCase()
                .split(/[^a-z]+/)
                .some((t) => words.has(t)),
        );
    let lat = columnIndex(headers, LAT);
    let lng = columnIndex(headers, LNG);
    if (lat < 0) lat = byWord(LAT_WORDS);
    if (lng < 0) lng = byWord(LNG_WORDS);
    if (lat >= 0 && lng >= 0 && lat !== lng) return { lat, lng };
    // No usable names: the values decide.
    const sample = rows.slice(header + 1, header + 21);
    if (sample.length < 3) return { lat: -1, lng: -1 };
    const width = Math.max(...sample.map((r) => r.length));
    const share = (col: number, ok: (n: number) => boolean) =>
        sample.filter((r) => {
            const t = String(r[col] ?? '').trim();
            const n = Number(t);
            return /\.\d/.test(t) && Number.isFinite(n) && ok(n);
        }).length / sample.length;
    let latCol = -1;
    let lngCol = -1;
    for (let c = 0; c < width; c++) {
        if (latCol < 0 && share(c, (n) => n >= 17 && n <= 72) >= 0.8) latCol = c;
        else if (lngCol < 0 && share(c, (n) => n >= -180 && n <= -60) >= 0.8) lngCol = c;
    }
    return latCol >= 0 && lngCol >= 0 ? { lat: latCol, lng: lngCol } : { lat: -1, lng: -1 };
}

/** The header row: the first of the top ten rows with a latitude and a longitude column; -1 when none. */
export function headerRowOf(rows: string[][]): number {
    for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const c = coordinateColumns(rows, i);
        if (c.lat >= 0 && c.lng >= 0) return i;
    }
    return -1;
}

/** True when a sheet or file has latitude and longitude columns. */
export function isLocationTable(rows: string[][]): boolean {
    return headerRowOf(rows) >= 0;
}

/**
 * Reads placement rows (from a CSV or a spreadsheet sheet): latitude and
 * longitude columns required, type, radius and a test / control column optional.
 * The header may sit below a title row or two.
 */
export function parsePlacementRows(
    rows: string[][],
    opts: { fallbackType?: string } = {},
): ParsedPlacements {
    const h = headerRowOf(rows);
    if (h < 0) {
        return {
            placements: [],
            errors: [
                'The file needs a latitude column (lat or latitude) and a longitude column (lng, lon or longitude).',
            ],
            radiusFromType: 0,
        };
    }
    const headers = rows[h].map((c) => String(c ?? ''));
    const { lat: latCol, lng: lngCol } = coordinateColumns(rows, h);
    const typeCol = columnIndex(headers, TYPE_HEADERS);
    const radiusCol = columnIndex(headers, RADIUS_HEADERS);
    const groupCol = columnIndex(headers, GROUP);
    const storeCol = columnIndex(headers, STORE_NAME);
    const brandCol = columnIndex(headers, BRAND_FUEL);
    const unknownNames: { store: boolean | null; brand: boolean | null }[] = [];

    const placements: Placement[] = [];
    const roles: (ListRole | null)[] = [];
    const errors: string[] = [];
    let radiusFromType = 0;
    const typedRadius = new Set<number>();
    rows.slice(h + 1).forEach((row, i) => {
        const rowNo = h + i + 2;
        const cell = (c: number) => (c >= 0 ? String(row[c] ?? '').trim() : '');
        if (row.every((c) => !String(c ?? '').trim())) return;
        const lat = Number(cell(latCol));
        const lng = Number(cell(lngCol));
        if (
            !cell(latCol) ||
            !cell(lngCol) ||
            !Number.isFinite(lat) ||
            lat < -90 ||
            lat > 90 ||
            !Number.isFinite(lng) ||
            lng < -180 ||
            lng > 180
        ) {
            errors.push(`Row ${rowNo}: latitude and longitude must be numbers on the map.`);
            return;
        }
        const type = cell(typeCol) || opts.fallbackType || undefined;
        const rawRadius = cell(radiusCol);
        let radiusMeters: number;
        if (rawRadius) {
            const r = Math.round(Number(rawRadius));
            if (!Number.isFinite(r) || r < 10 || r > 1000) {
                errors.push(`Row ${rowNo}: radius must be 10–1000 meters.`);
                return;
            }
            radiusMeters = r;
        } else {
            radiusMeters = radiusForType(type);
            radiusFromType += 1;
            typedRadius.add(placements.length);
        }
        placements.push({ lat, lng, radiusMeters, ...(type ? { type: type.slice(0, 80) } : {}) });
        roles.push(groupCol >= 0 ? roleOf(cell(groupCol)) : null);
        unknownNames.push({
            store: storeCol >= 0 ? UNKNOWN_NAME.test(cell(storeCol)) : null,
            brand: brandCol >= 0 ? UNKNOWN_NAME.test(cell(brandCol)) : null,
        });
    });
    if (placements.length > MAX_OOH_PLACEMENTS) {
        // A list often repeats a place per creative or week (M4S-5904: 9,697 rows, 1,214
        // places). Over the cap, each place is kept once, with its own group and names.
        const seen = new Set<string>();
        const keep: number[] = [];
        placements.forEach((p, i) => {
            const key = `${p.lat.toFixed(6)},${p.lng.toFixed(6)},${p.radiusMeters},${roles[i] ?? ''}`;
            if (!seen.has(key)) {
                seen.add(key);
                keep.push(i);
            }
        });
        if (keep.length > MAX_OOH_PLACEMENTS) {
            errors.push(
                `${keep.length.toLocaleString('en-US')} different locations is more than one list takes (${MAX_OOH_PLACEMENTS.toLocaleString('en-US')}). Split the list by market.`,
            );
            return { placements: [], errors, radiusFromType: 0 };
        }
        const dropped = placements.length - keep.length;
        const kept = keep.map((i) => placements[i]);
        return {
            placements: kept,
            errors,
            radiusFromType: keep.filter((i) => typedRadius.has(i)).length,
            duplicatesDropped: dropped,
            ...(groupCol >= 0 && roles.some((r) => r != null)
                ? { roles: keep.map((i) => roles[i]) }
                : {}),
            ...(storeCol >= 0 || brandCol >= 0
                ? { unknownNames: keep.map((i) => unknownNames[i]) }
                : {}),
        };
    }
    return {
        placements,
        errors,
        radiusFromType,
        ...(groupCol >= 0 && roles.some((r) => r != null) ? { roles } : {}),
        ...(storeCol >= 0 || brandCol >= 0 ? { unknownNames } : {}),
    };
}

/** Rows of a CSV, as text cells. */
export function csvRows(text: string): string[][] {
    return Papa.parse<string[]>(text.replace(/^\uFEFF/, ''), { skipEmptyLines: 'greedy' }).data.map(
        (r) => r.map((c) => String(c ?? '')),
    );
}

/** Reads a placement CSV: latitude and longitude columns required, type and radius optional. */
export function parsePlacementsCsv(
    text: string,
    opts: { fallbackType?: string } = {},
): ParsedPlacements {
    return parsePlacementRows(csvRows(text), opts);
}

/** "120 placements: 80 billboard, 40 bus shelter" — the read-back line. */
export function describePlacements(placements: Placement[]): string {
    const byType = new Map<string, number>();
    for (const p of placements) {
        const t = p.type?.toLowerCase() ?? 'with no type';
        byType.set(t, (byType.get(t) ?? 0) + 1);
    }
    const top = [...byType.entries()].sort((a, b) => b[1] - a[1]);
    const shown = top.slice(0, 3).map(([t, n]) => `${n.toLocaleString('en-US')} ${t}`);
    if (top.length > 3) shown.push(`${top.length - 3} more types`);
    const radii = placements.map((p) => p.radiusMeters);
    const min = Math.min(...radii);
    const max = Math.max(...radii);
    return `${placements.length.toLocaleString('en-US')} placement${placements.length === 1 ? '' : 's'} (${shown.join(', ')}; ${min === max ? `${min} m` : `${min}–${max} m`} geofence)`;
}

export const PLACEMENT_CSV_TEMPLATE =
    'latitude,longitude,type,radius_m\n40.758,-73.9855,Digital Spectacular,\n34.0522,-118.2437,Bus Shelter,\n41.8781,-87.6298,Billboard,150\n';
